import React, { useEffect, useState } from 'react'
import { Loader2, Check } from 'lucide-react'
import { Card, CardContent } from '@/components/ui/card'

interface OverlayStep {
  label: string
  delayMs: number
}

const STEPS: OverlayStep[] = [
  { label: 'Connecting', delayMs: 0 },
  { label: 'Fetching', delayMs: 1500 },
  { label: 'Generating', delayMs: 3500 },
]

/**
 * BuildingOverlay — full-screen overlay shown while dashboard panel creation is in progress.
 * Rendered conditionally by DashboardCanvas when `dashboardPanels.isBuilding` is true.
 * Displays three simulated progress steps (Connecting → Fetching → Generating).
 * Progress is time-based, not backend-driven; the overlay closes when the parent stops rendering it.
 */
export const BuildingOverlay: React.FC = () => {
  const [activeStep, setActiveStep] = useState(0)

  useEffect(() => {
    setActiveStep(0)

    const timers: ReturnType<typeof setTimeout>[] = []

    STEPS.forEach((step, i) => {
      if (step.delayMs > 0) {
        const t = setTimeout(() => {
          setActiveStep(i)
        }, step.delayMs)
        timers.push(t)
      }
    })

    return () => {
      timers.forEach(t => clearTimeout(t))
    }
  }, [])

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-primary/40 backdrop-blur-sm"
      role="status"
      aria-live="polite"
      aria-label="Building your dashboard"
    >
      <Card className="w-80 shadow-xl">
        <CardContent className="pt-6 pb-6">
          <h2 className="text-base font-semibold text-primary mb-6 text-center">
            Building Your Dashboard
          </h2>
          <div className="flex flex-col gap-4">
            {STEPS.map((step, i) => {
              const isComplete = i < activeStep
              const isActive = i === activeStep
              const isPending = i > activeStep

              return (
                <div key={step.label} className="flex items-center gap-3">
                  <div
                    data-testid={`step-circle-${i}`}
                    className={`w-7 h-7 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-all duration-300 ${
                      isComplete
                        ? 'border-accent bg-accent'
                        : isActive
                          ? 'border-accent bg-transparent'
                          : 'border-border bg-transparent'
                    }`}
                  >
                    {isComplete && <Check size={14} className="text-white" />}
                    {isActive && <Loader2 size={14} className="animate-spin text-accent" />}
                    {isPending && <div className="w-2 h-2 rounded-full bg-border" />}
                  </div>
                  <span
                    className={`text-sm font-medium ${
                      isComplete || isActive ? 'text-primary' : 'text-muted-foreground'
                    }`}
                  >
                    {step.label}
                  </span>
                </div>
              )
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
